/**
 * Add or edit one ingredient — a building block, not a meal.
 *
 * THE PORTION IS THE POINT. Every macro on this form describes `portion_desc` and nothing
 * else, and a recipe line multiplies it. "4 oz" saved as if it were "100 g" is a ~15% error
 * on every plate the ingredient appears on, sodium included, so the field is required and
 * sits directly beside the name rather than being an afterthought at the bottom.
 *
 * Lookup FILLS this form; it does not save anything. FoodLookup is reused rather than
 * reimplemented — it already does USDA by name and Open Food Facts by barcode, and it
 * already shows the match and its provenance before anything is accepted. An external
 * database is an estimate; a USDA search for "ribeye" once returned tenderloin.
 */
import { useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type { WellnessIngredient } from '../../shared/types';
import FoodLookup, { type FoodHit } from './FoodLookup';
import Sheet from './Sheet';

const MACRO_FIELDS: [string, string, string][] = [
  ['calories', 'Calories', '210'],
  ['protein_g', 'Protein (g)', '35'],
  ['fat_g', 'Fat (g)', '12'],
  ['carb_g', 'Carbs (g)', '0'],
  ['sodium_mg', 'Sodium (mg)', '65'],
  ['potassium_mg', 'Potassium (mg)', '320'],
];

const inputCls =
  'min-h-11 w-full rounded-xl border border-border bg-canvas px-3 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage';

export default function IngredientEditor({
  ingredient,
  onClose,
  onSaved,
}: {
  ingredient: WellnessIngredient | null;
  onClose: () => void;
  /** Hands back the saved row so a caller can drop it straight into a recipe. */
  onSaved: (saved: WellnessIngredient) => void;
}) {
  const [name, setName] = useState(ingredient?.name ?? '');
  const [portion, setPortion] = useState(ingredient?.portion_desc ?? '');
  const [vals, setVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      MACRO_FIELDS.map(([k]) => {
        const v = ingredient?.[k as keyof WellnessIngredient];
        return [k, v === null || v === undefined ? '' : String(v)];
      })
    )
  );
  const [source, setSource] = useState(ingredient?.source ?? 'manual');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // '' is sent through; the Worker's num() turns it into null, never 0.
      const body = {
        name,
        portion_desc: portion,
        source,
        ...Object.fromEntries(MACRO_FIELDS.map(([k]) => [k, vals[k] ?? ''])),
      };
      const saved = ingredient
        ? await api.put<WellnessIngredient>(`/wellness/ingredients/${ingredient.id}`, body)
        : await api.post<WellnessIngredient>('/wellness/ingredients', body);
      onSaved(saved);
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.body.detail
          ? err.body.detail
          : 'That did not save. Try again?'
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!ingredient) return;
    setSaving(true);
    setError(null);
    try {
      await api.del(`/wellness/ingredients/${ingredient.id}`);
      onSaved(ingredient);
    } catch (err) {
      // The usual failure is "a recipe still uses this", which is a real answer with a real
      // next step, not an error to swallow.
      setError(
        err instanceof ApiRequestError && err.body.detail
          ? err.body.detail
          : 'That did not delete. Try again?'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title={ingredient ? 'Edit ingredient' : 'New ingredient'} onClose={onClose}>
      {/* DELIBERATELY OUTSIDE THE <form>. Nested inside it, Enter in the search box submits
          the ingredient form — saving a half-filled row on the way to looking one up. */}
      <FoodLookup
        onPick={(h: FoodHit) => {
          if (!name.trim()) setName(h.brand ? `${h.name} (${h.brand})` : h.name);
          // The portion comes across with the numbers. It is the field most likely to be
          // left at whatever was already typed, and it is the one that scales everything.
          if (h.portion_desc) setPortion(h.portion_desc);
          setSource(h.source === 'usda' || h.source === 'openfoodfacts' ? h.source : 'manual');
          setVals((prev) => ({
            ...prev,
            // A null from the source stays an empty field, never a 0.
            calories: h.calories === null ? '' : String(h.calories),
            protein_g: h.protein_g === null ? '' : String(h.protein_g),
            fat_g: h.fat_g === null ? '' : String(h.fat_g),
            carb_g: h.carb_g === null ? '' : String(h.carb_g),
            sodium_mg: h.sodium_mg === null ? '' : String(h.sodium_mg),
            potassium_mg: h.potassium_mg === null ? '' : String(h.potassium_mg),
          }));
        }}
      />

      <form className="mt-4 space-y-4" onSubmit={save}>
        {error && (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <label className="min-w-[10rem] flex-1 block">
            <span className="mb-1 block text-sm font-medium text-ink">Ingredient</span>
            <input
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sirloin"
              className={inputCls}
            />
          </label>
          <label className="w-32 block">
            <span className="mb-1 block text-sm font-medium text-ink">Portion</span>
            <input
              required
              value={portion}
              onChange={(e) => setPortion(e.target.value)}
              placeholder="4 oz"
              className={inputCls}
            />
          </label>
        </div>
        <p className="-mt-2 text-xs text-ink-soft">
          The numbers below are for <strong>one {portion.trim() || 'portion'}</strong>. A
          recipe multiplies them, so this has to match what you measured.
        </p>

        <div className="grid grid-cols-2 gap-3">
          {MACRO_FIELDS.map(([key, label, ph]) => (
            <label key={key} className="block">
              <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
              <input
                inputMode="numeric"
                value={vals[key] ?? ''}
                onChange={(e) => setVals((p) => ({ ...p, [key]: e.target.value }))}
                placeholder={ph}
                className={inputCls}
              />
            </label>
          ))}
        </div>

        <p className="text-xs text-ink-soft">
          Leave anything you don’t know blank. It stays blank rather than counting as zero,
          and every recipe built on it will say its totals are only part of the picture.
        </p>

        <button
          type="submit"
          disabled={saving}
          className="min-h-12 w-full rounded-full bg-sage px-4 font-medium text-white disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          {saving ? 'Saving…' : ingredient ? 'Save changes' : 'Add ingredient'}
        </button>

        {ingredient && (
          <button
            type="button"
            onClick={remove}
            disabled={saving}
            className="min-h-11 w-full rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Remove ingredient
          </button>
        )}
      </form>
    </Sheet>
  );
}
